import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getGameFromSlug } from "../api/user";
import { leagueSlugFromPath } from "./leaguePath";

// "Is the user inside a joined league right now, and which one?"
//
// The app shell (the left rail, the 3-dot menu, the bottom bar, the header) has to
// change the moment someone becomes a member, so it needs the answer without every
// screen having to pass it down. This reads it off the URL and confirms membership
// with the league itself.
//
// A league is addressed two ways — the revamped screens under /contest/<slug> and
// the per-league routes at /<slug>/… — and both must count, or the shell reverts to
// the main app's halfway through the league.
//
// ONE SHARED STORE, not one fetch per consumer. Four components read this hook, and
// giving each its own `useEffect([slug])` had two consequences:
//
//   • four identical requests for the same league on every league screen, and
//   • a shell that could not react to joining. The slug does not change when you
//     join from a league's own front door, so nothing re-ran and every consumer
//     kept the `isMember: false` it had already cached. The rail only switched
//     after a reload — while walking into a league you were already in worked,
//     because that fetch happened after the fact.
//
// So membership lives here, and the join flow tells this module about it (see
// primeLeagueContext).

// The route lists and the path -> slug rule moved to utils/leaguePath.js, which
// has no imports of its own. The API layer needs the same answer (to scope
// watchlist requests to the league you are standing in) and this module imports
// FROM the API layer, so the pure part had to live somewhere neither imports.
// Re-exported here so every existing caller keeps working.
export { leagueSlugFromPath, LEAGUE_SUB_ROUTES, CONTEST_NON_LEAGUE } from "./leaguePath";

// slug -> resolved game (or null for "looked, nothing there").
const cache = new Map();
// slug -> in-flight promise, so concurrent consumers share one request.
const pending = new Map();
const listeners = new Set();

const emit = () => listeners.forEach((fn) => fn());

const load = (slug) => {
  if (cache.has(slug)) return Promise.resolve(cache.get(slug));
  if (pending.has(slug)) return pending.get(slug);

  const request = getGameFromSlug({ slug })
    .then((res) => {
      // A miss answers with HTTP 200 + success:false, so read the body.
      const data = res?.data?.success === false ? null : res?.data?.data;
      return data?.id ? data : null;
    })
    .catch(() => null)
    .then((game) => {
      cache.set(slug, game);
      pending.delete(slug);
      emit();
      return game;
    });

  pending.set(slug, request);
  return request;
};

/**
 * Publish a league the caller has already fetched.
 *
 * This is what makes the shell react to joining without a reload: the join flow
 * re-reads the league to confirm the membership is real, and hands that same
 * response over here rather than making everyone fetch it again.
 */
export const primeLeagueContext = (slug, game) => {
  if (!slug) return;
  cache.set(slug, game?.id ? game : null);
  pending.delete(slug);
  emit();
};

/**
 * Forget what we know about a league (or all of them) and let consumers re-read.
 * Use after anything that changes the league itself — editing its settings, or
 * leaving it.
 */
export const refreshLeagueContext = (slug) => {
  if (slug) cache.delete(slug);
  else cache.clear();
  emit();
};

/**
 * { slug, game, isMember, isOrganizer, hasStarted } — or nulls outside a league.
 *
 * `isMember` is what the shell keys off: a visitor previewing a competition is NOT
 * in it yet, and must keep the main app's navigation.
 */
export const useLeagueContext = () => {
  const { pathname } = useLocation();
  const slug = leagueSlugFromPath(pathname);
  // Re-render when the store changes. The value itself is read from the cache
  // below, so every consumer sees the same answer at the same time.
  const [, bump] = useState(0);

  useEffect(() => {
    const listener = () => bump((n) => n + 1);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  useEffect(() => {
    if (slug) load(slug);
  }, [slug]);

  const game = slug ? cache.get(slug) ?? null : null;

  let userId = null;
  try {
    userId = JSON.parse(localStorage.getItem("userInfo") || "null")?.id ?? null;
  } catch (e) {
    userId = null;
  }

  const start = game?.start_date ? new Date(game.start_date) : null;

  return {
    slug,
    game,
    isMember: !!game?.isMember,
    isOrganizer: !!game && !!userId && String(game.user_id) === String(userId),
    hasStarted: !!start && !Number.isNaN(start.getTime()) && start <= new Date(),
  };
};

export default useLeagueContext;
